import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  AuthorizeLoginPassword,
  goToNextAuthStep,
  Logout,
} from "../../actions/auth";
import { ROUTES } from "../../constants/clientRoutes";
import { EVENTS } from "../../constants/events";
import { trackEvent } from "../../services/eventTracker";
import { useAppDispatch } from "../../store/hooks";
import { toErrorObjs } from "../../services/formatter";

// types 
import type { ErrorObj } from "../../types/errors";
import type { RootState } from "../../types/redux";
import type { PasswordFormValues } from "../../types/components/Password";

// ui components
import AuthLayout from "../../components/Layouts/AuthLayout";
import PasswordCard from "../../components/Login/PasswordCard";

const Password = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const username = useSelector((state: RootState) => state.auth.username);
  const isAuthenticated = useSelector(
    (state: RootState) => state.user.isAuthenticated
  );

  const [errors, setErrors] = useState<ErrorObj[] | null>(null);

  // Mount-only by design. Replaces `componentDidMount` from the class
  // component: the question is "was this user already authenticated when
  // they arrived", not "is the user authenticated at any point".
  // Adding isAuthenticated to the deps re-fires this after a *successful*
  // login (Authenticated + FaceCheckEnrollment), logging the user out and
  // forcing a page reload instead of loading the FaceCheck SDK.
  // The ref also makes this safe to run twice, since React 18's StrictMode
  // deliberately fires effects twice in development.
  const authCheckedRef = useRef(false);

  useEffect(() => {
    if (authCheckedRef.current) return;
    authCheckedRef.current = true;

    if (isAuthenticated) {
      dispatch((Logout as any)());
      location.href = ROUTES.AUTH.LOGIN;
    }
  }, []);

  const onSubmit = useCallback(
    async ({ password }: PasswordFormValues): Promise<void> => {
      setErrors(null);

      try {
        // TODO: remove casts when actions/auth.js is converted to TS
        await dispatch((AuthorizeLoginPassword as any)(password));
        trackEvent(EVENTS.LOGIN.PASSWORD_SUBMITTED, { success: true });
        dispatch((goToNextAuthStep as any)(history));
      } catch (error: unknown) {
        const errorObjs = toErrorObjs(error);
        trackEvent(EVENTS.LOGIN.PASSWORD_SUBMITTED, {
          success: false,
          failure_reason: errorObjs[0].errorMessage,
        });
        setErrors(errorObjs);
      }
    },
    [dispatch, history]
  );

  const forgotPasswordLink = username
    ? `${ROUTES.SETTINGS.FORGOT_PASSWORD}/${username}`
    : ROUTES.SETTINGS.FORGOT_PASSWORD;

  return (
    <AuthLayout backActionRoute={ROUTES.AUTH.LOGIN}>
      <PasswordCard
        onSubmit={onSubmit}
        errors={errors}
        username={username}
        forgotPasswordLink={forgotPasswordLink}
      />
    </AuthLayout>
  );
};

export default Password;